<script setup lang="ts">
/**
 * Отмена встречи с причиной (DEV-2200 — вынесена из шапки).
 *
 * Одна модалка на два места: шапка отменяет открытый созвон, панель серии —
 * всю серию «эту и все следующие» по ближайшему. Причину спрашиваем всегда:
 * её увидят в письме отмены все, кого звали, и «встреча отменена» без слова
 * почему рождает ровно тот же вопрос в личке.
 */
import { AppButton, AppModal, AppTextField, useToast } from '@vinprop/design-system'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMeetingStore } from '../store'

import type { MeetingScope, MeetingSession } from '../api'

const props = defineProps<{
  meeting: MeetingSession
  /**
   * Область, решённая вызывающим: панель отменяет серию целиком, и выбор
   * здесь не показывается — иначе «Отменить серию» могло бы отменить один
   * созвон. Без неё область выбирает человек.
   */
  scope?: MeetingScope
  /** Заголовок и кнопка: «Отменить встречу» по умолчанию, у панели — серию */
  title?: string
}>()

const emit = defineEmits<{ close: []; cancelled: [] }>()

const { t } = useI18n()
const toast = useToast()
const store = useMeetingStore()

const SCOPES: MeetingScope[] = ['MEETING_SCOPE_THIS_ONLY', 'MEETING_SCOPE_THIS_AND_FOLLOWING']

const scope = ref<MeetingScope>(props.scope ?? 'MEETING_SCOPE_THIS_ONLY')
const isScopeChosen = !!props.meeting.seriesId && !props.scope
const heading = props.title ?? t('meetings.actions.cancelTitle')
const reason = ref('')
const isBusy = ref(false)

const cancel = async () => {
  isBusy.value = true

  try {
    await store.cancel(props.meeting.id, reason.value.trim(), scope.value)
    toast.success(t('meetings.actions.cancelled'))
    emit('cancelled')
  } catch {
    toast.error(t('meetings.actions.cancelFailed'))
  } finally {
    isBusy.value = false
  }
}
</script>

<template>
  <AppModal model-value :title="heading" size="small" @update:model-value="emit('close')">
    <p class="mcancel__body">{{ t('meetings.actions.cancelBody') }}</p>

    <fieldset v-if="isScopeChosen" class="mcancel__scope">
      <label v-for="option in SCOPES" :key="option" class="mcancel__scope-row">
        <input v-model="scope" type="radio" name="cancel-scope" :value="option" />
        <span>
          {{ t(`meetings.series.scope.${option === 'MEETING_SCOPE_THIS_ONLY' ? 'one' : 'rest'}`) }}
        </span>
      </label>
    </fieldset>

    <AppTextField
      v-model="reason"
      :label="t('meetings.actions.cancelReason')"
      :placeholder="t('meetings.actions.cancelReasonPlaceholder')"
    />

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('meetings.actions.cancelKeep') }}
      </AppButton>

      <AppButton :disabled="isBusy" @click="cancel()">{{ heading }}</AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.mcancel__body {
  margin: 0 0 12px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
}

.mcancel__scope {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 12px;
  padding: 0;
  border: none;
}

.mcancel__scope-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
  cursor: pointer;
}
</style>
